import { createSelector } from 'reselect';
import {
  selectAuthState,
  selectAuthUid,
  selectAuthEmail,
  selectIsAuthLoading,
  selectIsLoggedIn as selectIsLoggedInFromAuth
} from '../auth/selectors';
import { selectBillingState } from '../billing/selectors';

// Base selector
export const selectUserState = (state) => state.user || {};

// Re-export auth selectors for backward compatibility
// uid and email are now in the auth slice
export const selectUserUid = selectAuthUid;
export const selectUserEmail = selectAuthEmail;
export const selectIsAuthStateLoading = selectIsAuthLoading;
export const selectIsLoggedIn = selectIsLoggedInFromAuth;

// Simple selectors returning primitive values
export const selectUserRole = (state) => state.user ? state.user.role : null;
export const selectUserFullName = (state) => state.user ? state.user.fullName : '';
export const selectIsDemoMode = (state) => state.user ? state.user.isDemoMode : false;
export const selectAccountId = (state) => state.user ? state.user.accountId : null;
export const selectCompanyName = (state) => state.user ? state.user.companyName : '';

// Memoized selector that combines auth + user profile + billing data
// This maintains backward compatibility for components using selectUser
export const selectUser = createSelector(
  [selectAuthState, selectUserState, selectBillingState],
  (authState, userState, billingState) => {
    if (!authState.uid) return null;

    // Return combined auth + profile + billing data for backward compatibility
    return {
      uid: authState.uid,
      email: authState.email,
      fullName: userState.fullName,
      role: userState.role,
      isDemoMode: userState.isDemoMode,
      IsPhoneVerified: userState.IsPhoneVerified,
      Phone: userState.Phone,
      accounts: userState.accounts,
      accountId: userState.accountId,
      companyName: userState.companyName,
      accountSettings: userState.accountSettings,
      accountUsage: userState.accountUsage,
      billing: billingState,  // Include from billing module for backward compat
      tags: userState.tags,
      defaultUnit: userState.defaultUnit
    };
  }
);

export const selectAccounts = createSelector(
  [selectUserState],
  (userState) => userState.accounts || []
);

export const selectAccountSettings = createSelector(
  [selectUserState],
  (userState) => userState.accountSettings || null
);

export const selectAccountUsage = createSelector(
  [selectUserState],
  (userState) => userState.accountUsage || null
);

export const selectTags = createSelector(
  [selectUserState],
  (userState) => userState.tags || []
);

export const selectDefaultUnit = createSelector(
  [selectUserState],
  (userState) => userState.defaultUnit || null
);

// Memoized selector for current account's company name
export const selectCurrentAccountCompanyName = createSelector(
  [selectAccounts, selectAccountId],
  (accounts, accountId) => {
    const currentAccount = accounts?.find(acc => acc._id === accountId);
    return currentAccount?.CompanyName || '';
  }
);